import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../../baseQuery/basePharmaNavigatorQuery';
import { addNotification } from '../../notification/slices/notificationSlice';
import { NotificationType } from '../../notification/types';
import { setConsentState } from './eConsentPlatformSlice';
import { i18n } from '@senderra/translations';
import { LoginErrors } from '../../auth/components/Auth0RedirectHandler';
import { EConsentResponse, SigningHistoryEntry, SigningHistoryPayload } from './types';

export const eConsentPharmaNavigatorApi = createApi({
    reducerPath: 'eConsentPlatformApi',
    baseQuery,
    tagTypes: ['eConsentPlatform', 'signingHistory'],
    endpoints: (builder) => ({
        updateEConsentStatus: builder.mutation<void, FormData>({
            query: (formData) => ({
                url: '/api/v1/me/consent/accept',
                method: 'POST',
                body: formData,
            }),
        }),
        getEConsentStatus: builder.query<{ isConsentAccepted: boolean }, void>({
            query: () => '/api/v1/me/consent',
            providesTags: ['eConsentPlatform'],
            async onQueryStarted(id, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(setConsentState(data.isConsentAccepted));
                } catch (err) {
                    if ((err as { [key: string]: any })?.error?.data !== LoginErrors.INACTIVE_TENANT) {
                        dispatch(
                            addNotification({ message: i18n.t('common-error-message'), type: NotificationType.ERROR })
                        );
                    }
                    dispatch(setConsentState(false));
                }
            },
        }),
        getSigningHistory: builder.query<EConsentResponse<SigningHistoryEntry>, SigningHistoryPayload>({
            query: (payload) => ({
                url: `/api/v1/admin/tenant/${payload.tenantId}/document-signatures?${payload.filterQuery}`,
            }),
            providesTags: ['signingHistory'],
        }),
        getSigningHistoryPDF: builder.query<{ url: string; filename: string }, SigningHistoryPayload>({
            query: (payload) => ({
                url: `/api/v1/admin/tenant/${payload.tenantId}/document-signatures/download?${payload.filterQuery}`,
                responseHandler: async (response) => {
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);

                    const contentDisposition = response.headers.get('Content-Disposition');
                    const match = contentDisposition?.match(/filename="?([^";]+)"?/);
                    const filename = match ? match[1] : `PharmaNavigator-EConsents.pdf`;

                    return { url, filename };
                },
            }),
        }),
        getSigningHistoryCSV: builder.query<{ url: string; filename: string }, SigningHistoryPayload>({
            query: (payload) => ({
                url: `/api/v1/admin/tenant/${payload.tenantId}/document-signatures/export?${payload.filterQuery}`,
                responseHandler: async (response) => {
                    const blob = await response.blob();
                    const url = URL.createObjectURL(blob);

                    const contentDisposition = response.headers.get('Content-Disposition');
                    const match = contentDisposition?.match(/filename="?([^";]+)"?/);
                    const filename = match ? match[1] : `PharmaNavigator-EConsents.csv`;

                    return { url, filename };
                },
            }),
        }),
    }),
});

export const {
    useGetEConsentStatusQuery,
    useGetSigningHistoryQuery,
    useLazyGetSigningHistoryPDFQuery,
    useLazyGetSigningHistoryCSVQuery,
    useUpdateEConsentStatusMutation,
} = eConsentPharmaNavigatorApi;
